import { Injectable } from "@angular/core";
import { importSPKI, jwtVerify } from "jose";
import { BehaviorSubject } from "rxjs";
import { configuracion } from "src/configuracion";

const JWKS_ENDPOINT = `${configuracion.servidor_api}/.well-known/jwks.json`

export type MunicipalityTokenPayload = {
  modulos: string;
  permisos: string;
  id_rol: number;
  nombre_rol: string;
  idMunicipio: number;
  iat: number;
  iss: string;
  aud: string;
  exp: number;
  nbf: number;
};

export type MunicipalityTokenStatus = "idle" | "loading" | "ready" | "expired" | "error";

@Injectable({
  providedIn: "root",
})
export class MunicipalityTokenService {
  private publicKey: CryptoKey | null = null;
  private municipalityToken: MunicipalityTokenPayload | null = null;
  private municipalityTokenSubject = new BehaviorSubject<MunicipalityTokenPayload | null>(null);
  private rawToken: string | null = null;
  private tokenStatus = new BehaviorSubject<MunicipalityTokenStatus>("idle");

  private async loadPublicKeyPem() {
    try {
      
      const publicKeyUrl = JWKS_ENDPOINT;
      
      
      const response = await fetch(publicKeyUrl.toString(), { cache: "no-store" });

      if (!response.ok) {
        throw new Error(`Error en la petición para cargar: ${response.status}`);
      }

      const data = await response.json();
      
      const pem = (data.jwk).trim();

      if (!pem) {
        throw new Error("El archivo de llave pública está vacío");
      }

      return pem;
    } catch {
    }
  }

  async initialize() {
    if (!this.publicKey) {
      const publicKeyPem = await this.loadPublicKeyPem();
      this.publicKey = await importSPKI(
        publicKeyPem,
        "EdDSA",
      );
    }

    // this.tokenStatus.subscribe((status) => printDebug(`TOKEN STATUS: ${status}`, this.initialize.name))
  }

  getStatus() {
    return this.tokenStatus.value;
  }

  getToken$() {
    return this.municipalityTokenSubject.asObservable();
  }

  isReady() {
    return this.tokenStatus.value === "ready";
  }

  waitUntilReady(timeoutMs = 5000): Promise<boolean> {
    if (this.isReady()) {
      return Promise.resolve(true);
    }

    if (this.tokenStatus.value === "error") {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const subscription = this.tokenStatus.subscribe((status) => {
        if (status === "ready") {
          subscription.unsubscribe();
          resolve(true);
        }

        if (status === "error") {
          subscription.unsubscribe();
          resolve(false);
        }
      });

      if (timeoutMs > 0) {
        setTimeout(() => {
          subscription.unsubscribe();
          resolve(this.isReady());
        }, timeoutMs);
      }
    });
  }

  getModules() {
    return this.municipalityToken?.modulos;
  }

  getMunicipalityId() {
    return this.municipalityToken?.idMunicipio;
  }

  getUserMunicipalityRole() {
    return this.municipalityToken?.id_rol;
  }

  getUserMunicipalityRoleName() {
    return this.municipalityToken?.nombre_rol;
  }

  getMunicipalityPermissions() {
    return this.municipalityToken?.permisos;
  }

  getTokenExpiration(token: string) {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  }

  async verifyToken(token: string) {
    await this.initialize();

    if (!this.publicKey) throw new Error("No hay llave pública");

    return jwtVerify<unknown>(token, this.publicKey, {
      algorithms: ["EdDSA"],
      clockTolerance: 300,
    });
  }

  async verifyAndStoreToken(token: string): Promise<void> {
    this.tokenStatus.next("loading");

    await this.initialize();

    if (!this.publicKey) throw new Error("No hay llave pública");

    try {
      const { payload } = await this.verifyToken(token);

      this.rawToken = token;
      this.municipalityToken = payload as MunicipalityTokenPayload;
      this.municipalityTokenSubject.next(this.municipalityToken);

      this.tokenStatus.next("ready");

    } catch (error) {
      if (error.message.includes('claim timestamp check failed')) {
        this.rawToken = token;
        this.tokenStatus.next("expired");

      } else {
        this.rawToken = null;
        this.municipalityToken = null;
        this.municipalityTokenSubject.next(null);
        this.tokenStatus.next("error");
        throw error;
      }
    }
  }


  getToken(getRawToken = false) {
    if (getRawToken) {
      return this.rawToken;
    }

    return this.municipalityToken;
  }
}
